import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import CtaSection from '../components/CtaSection';
import { fetchCollection, getOptimizedImageUrl } from '../lib/cms';

export default function HomePage() {
  const [cases, setCases] = useState([]);
  const [loadingCases, setLoadingCases] = useState(true);

  useEffect(() => {
    fetchCollection('cases', { limit: 4, sort: 'order', order: 'asc' }).then((res) => {
      setCases(res.items || []);
      setLoadingCases(false);
    });
  }, []);

  const values = [
    {
      icon: 'fa-light fa-comments-question',
      title: 'Tæt sparring & rådgivning',
      desc: 'Vi giver jer konkret sparring på idéer, trends og formater, så jeres indhold rammer jeres målgruppe præcist.',
    },
    {
      icon: 'fa-light fa-video',
      title: 'I optager – eller vi mødes',
      desc: 'I fanger typisk hverdagen med smartphone efter vores vejledning – og ved nærmere aftale er vi fleksible til også at komme ud til jer. Vi klipper og optimerer indholdet.',
    },
    {
      icon: 'fa-light fa-eye',
      title: 'Målrettet synlighed & rækkevidde',
      desc: 'Få kontinuerlig tilstedeværelse på præcis de platforme I ønsker – Instagram, Facebook, TikTok, LinkedIn eller YouTube.',
    },
    {
      icon: 'fa-light fa-calendar-star',
      title: 'Meta SoMe postkalender',
      desc: 'Få struktur og ro i sindet med en opdateret postkalender, så jeres profiler altid udkommer kontinuerligt.',
    },
    {
      icon: 'fa-light fa-handshake-slash',
      title: '0 binding & fleksibilitet',
      desc: 'Ingen uoverskuelige eller stive kontrakter. Vi tilpasser løbende samarbejdet efter jeres forretnings aktuelle tempo.',
    },
  ];

  const steps = [
    {
      num: '01',
      title: 'Indledende sparring & dialog',
      desc: 'Vi afstemmer jeres forretningsmål, målgruppe og brand, så vi rammer præcis de rigtige emner og vinkler.',
    },
    {
      num: '02',
      title: 'Idéliste & vejledning',
      desc: 'I modtager en overskuelig skudliste og konkrete råd til nem optagelse med smartphone i jeres hverdag.',
    },
    {
      num: '03',
      title: 'I optager & sender råfiler',
      desc: 'I fanger scenerne når det passer jer og uploader nemt jeres råoptagelser direkte til os.',
    },
    {
      num: '04',
      title: 'Professionel redigering',
      desc: 'Vi klipper i 9:16 format, tilføjer hooks, undertekster og trending lyd, så indholdet fanger opmærksomheden.',
    },
    {
      num: '05',
      title: 'Meta kalender & synlighed',
      desc: 'Vi strukturerer og vedligeholder jeres postkalender i Meta med fængende tekster, så I er synlige uge efter uge.',
    },
  ];

  return (
    <>
      <SeoHead
        isHome={true}
        title="Myserviceforyou | SoMe Sparring, Redigering & Synlighed"
        description="Få sparring på idéer, professionel redigering og styrket synlighed på sociale medier. Vi styrer jeres Meta-postkalender og profiler med 0 binding."
      />

      {/* Hero Section - Full Width Layout */}
      <section className="relative min-h-[92vh] flex flex-col justify-center pt-40 pb-24 md:pt-48 md:pb-28 overflow-hidden bg-[#161514] text-[#FAF8F5]">
        {/* Background Full-Bleed Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="/cdn-cgi/image/width=2000,quality=85,format=auto/_assets/hero-myserviceforyou-content-creator.png"
            alt="Smartphone content creation og SoMe sparring i café og hotelmiljø"
            className="w-full h-full object-cover object-center opacity-70 scale-100 transition-opacity duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#161514] via-[#161514]/40 to-[#161514]/65" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-transparent via-[#161514]/25 to-[#161514]/70" />
        </div>

        {/* Central Hero Content */}
        <div className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8 sm:space-y-10 pt-4 sm:pt-6 md:pt-8">
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-[#FAF8F5] leading-[1.08] max-w-4xl mx-auto">
            Synlighed og salg på dine{' '}
            <span className="text-[#C5A880]">sociale medier</span>
          </h1>

          <p className="text-lg sm:text-xl text-[#D8D1C7] max-w-3xl mx-auto leading-relaxed font-light">
            Til <strong className="text-white font-medium">hospitality- og servicebranchen – fra restauranter og caféer til hoteller og servicefag</strong>: Få en dedikeret partner til <strong className="text-white font-medium">sparring, videoer, opslag, redigering og planlægning</strong> på dine valgte platforme. Altid 100% skræddersyet til dine ønsker – uden faste pakker og med 0 binding.
          </p>

          {/* Hvem, Hvad & Hvordan overblik */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto pt-3 text-left">
            {/* 1: Hvem */}
            <div className="p-6 rounded-2xl bg-[#161514]/80 hover:bg-[#1c1a18]/90 backdrop-blur-xl border border-white/20 shadow-xl flex flex-col justify-between transition-all group">
              <div>
                <div className="flex items-center justify-between gap-2 mb-3.5">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#C5A880]/15 border border-[#C5A880]/30 text-[11px] font-bold text-[#D8B98F] uppercase tracking-wider">
                    Målgruppe
                  </span>
                  <div className="w-9 h-9 rounded-xl bg-[#C5A880]/20 border border-[#C5A880]/35 flex items-center justify-center text-[#FAF8F5] text-sm shrink-0 group-hover:scale-105 transition-transform">
                    <i className="fa-regular fa-hotel text-xs" aria-hidden="true" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-white leading-snug">
                  Hoteller, caféer, butikker &amp; <br className="hidden sm:inline" />
                  ferieboliger
                </h3>
                <p className="text-sm text-[#E2DDD5] mt-2.5 leading-relaxed font-light">
                  For dig med en forretning, café, restaurant eller overnatningssted, som ønsker professionel synlighed og flere kunder – uden selv at skulle bruge tid på SoMe.
                </p>
              </div>
            </div>

            {/* 2: Hvad */}
            <div className="p-6 rounded-2xl bg-[#161514]/80 hover:bg-[#1c1a18]/90 backdrop-blur-xl border border-white/20 shadow-xl flex flex-col justify-between transition-all group">
              <div>
                <div className="flex items-center justify-between gap-2 mb-3.5">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#C5A880]/15 border border-[#C5A880]/30 text-[11px] font-bold text-[#D8B98F] uppercase tracking-wider">
                    Ydelsen
                  </span>
                  <div className="w-9 h-9 rounded-xl bg-[#C5A880]/20 border border-[#C5A880]/35 flex items-center justify-center text-[#FAF8F5] text-sm shrink-0 group-hover:scale-105 transition-transform">
                    <i className="fa-regular fa-wand-magic-sparkles text-xs" aria-hidden="true" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-white leading-snug">
                  Sparring · Redigering · Synlighed
                </h3>
                <p className="text-sm text-[#E2DDD5] mt-2.5 leading-relaxed font-light">
                  Både fængende videoer og flotte opslag, videoredigering, strategisk planlægning og tæt sparring – skabt med fokus på reel synlighed og engagement.
                </p>
              </div>
            </div>

            {/* 3: Hvordan */}
            <div className="p-6 rounded-2xl bg-[#161514]/80 hover:bg-[#1c1a18]/90 backdrop-blur-xl border border-white/20 shadow-xl flex flex-col justify-between transition-all group">
              <div>
                <div className="flex items-center justify-between gap-2 mb-3.5">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#C5A880]/15 border border-[#C5A880]/30 text-[11px] font-bold text-[#D8B98F] uppercase tracking-wider">
                    Samarbejdet
                  </span>
                  <div className="w-9 h-9 rounded-xl bg-[#C5A880]/20 border border-[#C5A880]/35 flex items-center justify-center text-[#FAF8F5] text-sm shrink-0 group-hover:scale-105 transition-transform">
                    <i className="fa-regular fa-sliders text-xs" aria-hidden="true" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-white leading-snug">
                  Skræddersyet til jeres behov
                </h3>
                <p className="text-sm text-[#E2DDD5] mt-2.5 leading-relaxed font-light">
                  Samarbejdet tager altid udgangspunkt i, hvad netop jeres firma ønsker og mangler. Planen og ydelserne sammensættes 100% ud fra jeres unikke mål og behov.
                </p>
              </div>
            </div>
          </div>

          <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4 max-w-md sm:max-w-none mx-auto">
            <Link
              to="/kontakt#kontaktformular"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-9 py-4.5 rounded-full bg-[#FAF8F5] text-[#161514] font-semibold text-base hover:bg-[#EAE4DC] active:scale-98 transition-all shadow-xl hover:shadow-2xl"
            >
              <i className="fa-regular fa-paper-plane text-sm" aria-hidden="true" />
              <span>Gå til kontaktformular</span>
              <i className="fa-regular fa-arrow-right text-sm" aria-hidden="true" />
            </Link>
            <Link
              to="/saadan-arbejder-vi"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-8 py-4.5 rounded-full border border-white/20 text-[#FAF8F5] font-medium text-base hover:bg-white/10 transition-colors"
            >
              <span>Sådan arbejder vi</span>
            </Link>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-y-2 gap-x-6 text-xs sm:text-sm text-[#C4BEB5] font-light pt-1">
            <span className="inline-flex items-center gap-1.5">
              <i className="fa-regular fa-check text-[#C5A880] text-xs" aria-hidden="true" />
              Uforpligtende dialog
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="fa-regular fa-check text-[#C5A880] text-xs" aria-hidden="true" />
              0 binding
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="fa-regular fa-check text-[#C5A880] text-xs" aria-hidden="true" />
              Ingen faste pakker – kun 1:1 aftaler
            </span>
          </div>
        </div>
      </section>

      {/* Campaign Bar */}
      <section className="py-4 bg-[#876F50] text-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
          <div className="flex items-center gap-2.5">
            <i className="fa-light fa-sparkles text-amber-200 text-lg" aria-hidden="true" />
            <p className="text-xs sm:text-sm font-semibold tracking-wide">
              Sparring, planlægning, videoredigering og meget mere på dine valgte platforme
            </p>
          </div>
          <Link
            to="/kontakt"
            className="text-xs font-bold text-white uppercase tracking-wider underline underline-offset-4 hover:text-amber-200 transition-colors shrink-0"
          >
            Hør om dine muligheder &rarr;
          </Link>
        </div>
      </section>

      {/* Kort introduktion om forretningskonceptet */}
      <section className="py-20 md:py-24 bg-[#FAFAF8] border-b border-[#E8E2D9]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-[#161514] leading-snug">
            I kender jeres forretning bedst.
            <br />
            <span className="text-[#876F50] font-normal">Vi hjælper jer med at få den vist frem på de sociale medier.</span>
          </h2>

          <p className="text-base sm:text-lg text-[#5C5852] leading-relaxed max-w-3xl mx-auto">
            Hverken du eller dine medarbejdere behøver bruge timevis på at klippe videoer, udtænke undertekster eller stresse over, hvornår der sidst blev postet. Gennem tæt sparring og vejledning guider vi jer til gode optagelser med jeres smartphone (og ved nærmere aftale er vi fleksible til at komme ud til jer). I sender råmaterialet til os, og vi sørger for professionel redigering samt planlægning på alle de platforme, I ønsker at være synlige på.
          </p>
        </div>
      </section>

      {/* Værdikæden: Synlighed, Engagement & Salg */}
      <section className="py-20 md:py-28 bg-[#FFFFFF] border-b border-[#E8E2D9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#F5F1EB] border border-[#E5DFD6] text-xs font-semibold uppercase tracking-widest text-[#876F50]">
              <i className="fa-light fa-sparkles text-xs" aria-hidden="true" />
              <span>Effekten af gennemtænkt content</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-[#161514]">
              Sådan skaber content mere synlighed, engagement og salg
            </h2>
            <p className="text-base text-[#5C5852] leading-relaxed">
              Godt videoindhold handler ikke om tomme visninger. Det handler om en klar strategisk tragt, der forvandler nysgerrige seere til loyale, betalende kunder.
            </p>
          </div>

          {/* 3 Value Pillars */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* 1. Synlighed */}
            <div className="bg-[#FAF8F5] border border-[#E5DFD6] rounded-3xl p-8 space-y-6 flex flex-col justify-between hover:border-[#C5A880] transition-colors group">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="w-14 h-14 rounded-2xl bg-white border border-[#E5DFD6] flex items-center justify-center text-[#876F50] text-2xl group-hover:scale-105 transition-transform shadow-xs">
                    <i className="fa-light fa-eye" aria-hidden="true" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-white border border-[#E5DFD6] text-[#876F50]">
                    Trin 1 · Rækkevidde
                  </span>
                </div>

                <div className="space-y-2">
                  <h3 className="text-2xl font-bold text-[#161514] tracking-tight">
                    Maksimal synlighed
                  </h3>
                  <p className="text-sm font-semibold text-[#876F50]">
                    Bryd igennem algoritmen og bliv set
                  </p>
                </div>

                <p className="text-sm text-[#5C5852] leading-relaxed">
                  Med korte videoer i 9:16-format, fængende hooks i de første 3 sekunder og dynamiske undertekster fanger vi opmærksomheden i feedet. I bryder igennem Meta- og TikTok-algoritmen og bliver top-of-mind hos jeres målgruppe.
                </p>
              </div>

              <div className="pt-4 border-t border-[#EAE4DC] space-y-2.5">
                <p className="text-xs uppercase tracking-wider font-bold text-[#7A746B]">
                  Konkret effekt:
                </p>
                <ul className="space-y-2 text-xs text-[#4A4640]">
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#876F50] text-xs shrink-0" aria-hidden="true" />
                    <span>Høj rækkevidde hos potentielle kunder</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#876F50] text-xs shrink-0" aria-hidden="true" />
                    <span>Konsekvent tilstedeværelse i målgruppens feed</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#876F50] text-xs shrink-0" aria-hidden="true" />
                    <span>Styrket brandkendskab lokalt og nationalt</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* 2. Engagement */}
            <div className="bg-[#FAF8F5] border border-[#E5DFD6] rounded-3xl p-8 space-y-6 flex flex-col justify-between hover:border-[#C5A880] transition-colors group">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="w-14 h-14 rounded-2xl bg-white border border-[#E5DFD6] flex items-center justify-center text-[#876F50] text-2xl group-hover:scale-105 transition-transform shadow-xs">
                    <i className="fa-light fa-comments" aria-hidden="true" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-white border border-[#E5DFD6] text-[#876F50]">
                    Trin 2 · Relation
                  </span>
                </div>

                <div className="space-y-2">
                  <h3 className="text-2xl font-bold text-[#161514] tracking-tight">
                    Dybere engagement
                  </h3>
                  <p className="text-sm font-semibold text-[#876F50]">
                    Opbyg tillid med autentisk hverdagsindhold
                  </p>
                </div>

                <p className="text-sm text-[#5C5852] leading-relaxed">
                  Folk scroller forbi polerede standardannoncer, men stopper ved ægte mennesker og godt håndværk. Når I viser hverdagen og personerne bag, skabes en ægte relation, hvor følgerne kommenterer, deler og får dyb tillid til jer.
                </p>
              </div>

              <div className="pt-4 border-t border-[#EAE4DC] space-y-2.5">
                <p className="text-xs uppercase tracking-wider font-bold text-[#7A746B]">
                  Konkret effekt:
                </p>
                <ul className="space-y-2 text-xs text-[#4A4640]">
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#876F50] text-xs shrink-0" aria-hidden="true" />
                    <span>Autentisk indhold slår kolde reklamer</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#876F50] text-xs shrink-0" aria-hidden="true" />
                    <span>Flere kommentarer, likes og delinger</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#876F50] text-xs shrink-0" aria-hidden="true" />
                    <span>Tæt relation og høj troværdighed</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* 3. Salg */}
            <div className="bg-[#FAF8F5] border border-[#E5DFD6] rounded-3xl p-8 space-y-6 flex flex-col justify-between hover:border-[#C5A880] transition-colors group">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="w-14 h-14 rounded-2xl bg-white border border-[#E5DFD6] flex items-center justify-center text-[#876F50] text-2xl group-hover:scale-105 transition-transform shadow-xs">
                    <i className="fa-light fa-chart-line-up" aria-hidden="true" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-white border border-[#E5DFD6] text-[#876F50]">
                    Trin 3 · Bundlinje
                  </span>
                </div>

                <div className="space-y-2">
                  <h3 className="text-2xl font-bold text-[#161514] tracking-tight">
                    Målbart salg
                  </h3>
                  <p className="text-sm font-semibold text-[#876F50]">
                    Forvandl følgere til betalende kunder
                  </p>
                </div>

                <p className="text-sm text-[#5C5852] leading-relaxed">
                  Når synlighed og tillid er etableret, omsættes opmærksomheden direkte til handling. Kunderne vælger jer, når behovet opstår, fordi de allerede har set jeres arbejde og stoler på jer. Profilen fungerer som jeres digitale sælger døgnet rundt.
                </p>
              </div>

              <div className="pt-4 border-t border-[#EAE4DC] space-y-2.5">
                <p className="text-xs uppercase tracking-wider font-bold text-[#7A746B]">
                  Konkret effekt:
                </p>
                <ul className="space-y-2 text-xs text-[#4A4640]">
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#876F50] text-xs shrink-0" aria-hidden="true" />
                    <span>Flere henvendelser i indbakken (DMs) og opkald</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#876F50] text-xs shrink-0" aria-hidden="true" />
                    <span>Flere bookinger, reservationer og butikskunder</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#876F50] text-xs shrink-0" aria-hidden="true" />
                    <span>Kortere salgsproces fordi kunden allerede er overbevist</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brancher & Målgrupper: Hoteller, Airbnb, Restauranter, Butikker m.fl. */}
      <section className="py-20 md:py-28 bg-[#FAFAF8] border-b border-[#E8E2D9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#EAE4DC] text-xs font-semibold uppercase tracking-widest text-[#4A4640]">
              <i className="fa-light fa-compass-drafting text-xs" aria-hidden="true" />
              <span>Hvem skaber vi synlighed for?</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-[#161514]">
              Skræddersyet til brancher, hvor stemning og gæster er i centrum
            </h2>
            <p className="text-base text-[#5C5852] leading-relaxed">
              Uanset om du driver et boutique-hotel, udlejer en unik Airbnb-feriebolig, driver restaurant, butik – og mange flere i branchen inden for gæstfrihed, oplevelser og lokal service. Vi hjælper jer med at omsætte hverdagens øjeblikke til direkte bookinger og faste kunder.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* 1. Hoteller */}
            <div className="group bg-white border border-[#E5DFD6] rounded-3xl overflow-hidden shadow-xs hover:border-[#C5A880] transition-all flex flex-col justify-between">
              <div className="aspect-16/10 overflow-hidden relative bg-[#161514]">
                <img
                  src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/d61c94bc-c5ec-439d-a220-afcc00eef7a0/case-boutique-hotel.png', { width: 600, quality: 80, format: 'auto' })}
                  alt="Hoteller og boutique ophold"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-[#161514]/80 backdrop-blur-xs text-white text-xs font-semibold flex items-center gap-1.5">
                  <i className="fa-light fa-hotel text-xs text-[#C5A880]" aria-hidden="true" />
                  <span>Hoteller</span>
                </div>
              </div>
              <div className="p-6 space-y-3 flex-1 flex flex-col justify-between">
                <div className="space-y-2">
                  <h3 className="text-lg font-bold text-[#161514]">
                    Hoteller & Boutique Stays
                  </h3>
                  <p className="text-xs sm:text-sm text-[#5C5852] leading-relaxed">
                    Vis suiterne, morgenmadsbordet, spa-området og den rolige atmosfære. Væk rejselysten og konverter følgere til direkte værelsesbookinger uden om dyre platforme.
                  </p>
                </div>
                <div className="pt-3 border-t border-[#F0EBE3] text-xs font-semibold text-[#876F50] flex items-center gap-1">
                  <span>Flere direkte værelsesbookinger</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </div>
              </div>
            </div>

            {/* 2. Airbnb & Ferieboliger */}
            <div className="group bg-white border border-[#E5DFD6] rounded-3xl overflow-hidden shadow-xs hover:border-[#C5A880] transition-all flex flex-col justify-between">
              <div className="aspect-16/10 overflow-hidden relative bg-[#161514]">
                <img
                  src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/d61c94bc-c5ec-439d-a220-afcc00eef7a0/case-nordic-airbnb.png', { width: 600, quality: 80, format: 'auto' })}
                  alt="Airbnb og ferieboligudlejning"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-[#161514]/80 backdrop-blur-xs text-white text-xs font-semibold flex items-center gap-1.5">
                  <i className="fa-light fa-house-tree text-xs text-[#C5A880]" aria-hidden="true" />
                  <span>Airbnb</span>
                </div>
              </div>
              <div className="p-6 space-y-3 flex-1 flex flex-col justify-between">
                <div className="space-y-2">
                  <h3 className="text-lg font-bold text-[#161514]">
                    Airbnb & Ferieboliger
                  </h3>
                  <p className="text-xs sm:text-sm text-[#5C5852] leading-relaxed">
                    Sælg roen, nærværet og stemningen. Fyld kalenderen i ydersæsonen med fængende videoer af brændeovn, kaffe på terrassen og skøn natur omkring boligen.
                  </p>
                </div>
                <div className="pt-3 border-t border-[#F0EBE3] text-xs font-semibold text-[#876F50] flex items-center gap-1">
                  <span>Fuld belægning året rundt</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </div>
              </div>
            </div>

            {/* 3. Restauranter & Gastronomi */}
            <div className="group bg-white border border-[#E5DFD6] rounded-3xl overflow-hidden shadow-xs hover:border-[#C5A880] transition-all flex flex-col justify-between">
              <div className="aspect-16/10 overflow-hidden relative bg-[#161514]">
                <img
                  src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/d61c94bc-c5ec-439d-a220-afcc00eef7a0/case-nordic-dining.png', { width: 600, quality: 80, format: 'auto' })}
                  alt="Restauranter, caféer og gastronomi"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-[#161514]/80 backdrop-blur-xs text-white text-xs font-semibold flex items-center gap-1.5">
                  <i className="fa-light fa-utensils text-xs text-[#C5A880]" aria-hidden="true" />
                  <span>Gastronomi</span>
                </div>
              </div>
              <div className="p-6 space-y-3 flex-1 flex flex-col justify-between">
                <div className="space-y-2">
                  <h3 className="text-lg font-bold text-[#161514]">
                    Restauranter & Caféer
                  </h3>
                  <p className="text-xs sm:text-sm text-[#5C5852] leading-relaxed">
                    Sanselige madvideoer, råvarer i sæson og liv i spisesalen, der giver seerne mundvand og forvandler forbipasserende til faste gæster ved bordene.
                  </p>
                </div>
                <div className="pt-3 border-t border-[#F0EBE3] text-xs font-semibold text-[#876F50] flex items-center gap-1">
                  <span>Flere bordreservationer</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </div>
              </div>
            </div>

            {/* 4. Butikker & Service */}
            <div className="group bg-white border border-[#E5DFD6] rounded-3xl overflow-hidden shadow-xs hover:border-[#C5A880] transition-all flex flex-col justify-between">
              <div className="aspect-16/10 overflow-hidden relative bg-[#161514]">
                <img
                  src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/d61c94bc-c5ec-439d-a220-afcc00eef7a0/case-lifestyle-retail.png', { width: 600, quality: 80, format: 'auto' })}
                  alt="Butikker, livsstil og service"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-[#161514]/80 backdrop-blur-xs text-white text-xs font-semibold flex items-center gap-1.5">
                  <i className="fa-light fa-store text-xs text-[#C5A880]" aria-hidden="true" />
                  <span>Detail & Service</span>
                </div>
              </div>
              <div className="p-6 space-y-3 flex-1 flex flex-col justify-between">
                <div className="space-y-2">
                  <h3 className="text-lg font-bold text-[#161514]">
                    Detail & Servicevirksomheder
                  </h3>
                  <p className="text-xs sm:text-sm text-[#5C5852] leading-relaxed">
                    Fremvis jeres sortiment, unikt håndværk og menneskene bag disken. Skab personlig troværdighed, der trækker kunder direkte ind i forretningen.
                  </p>
                </div>
                <div className="pt-3 border-t border-[#F0EBE3] text-xs font-semibold text-[#876F50] flex items-center gap-1">
                  <span>Øget butikssalg & henvendelser</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 p-5 rounded-2xl bg-white border border-[#E5DFD6] flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#EAE4DC] text-[#876F50] text-sm">
                <i className="fa-regular fa-sparkles" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold text-[#161514]">
                  ... og mange flere i branchen
                </p>
                <p className="text-xs text-[#5C5852]">
                  Glamping, B&B, oplevelser, caféer, klinikker, showrooms, behandlere og servicefag.
                </p>
              </div>
            </div>
            <Link
              to="/cases"
              className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-[#876F50] hover:text-[#68543B] transition-colors"
            >
              <span>Se cases og brancher</span>
              <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/cases"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#161514] hover:text-[#876F50] transition-colors"
            >
              <span>Se konkrete eksempler og cases fra hoteller, Airbnb og mange flere i branchen</span>
              <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* Værdisektion / USPs */}
      <section className="py-20 md:py-28 bg-[#F5F1EB] border-b border-[#E8E2D9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-[#161514]">
              Gennemskueligt samarbejde <br className="hidden sm:inline" />med fokus på synlighed
            </h2>
            <p className="text-base text-[#5C5852]">
              Få det professionelle udtryk på sociale medier uden bøvl og uden dyre kamerahold på adressen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {values.map((v, i) => (
              <div
                key={i}
                className="bg-white border border-[#E5DFD6] rounded-3xl p-8 space-y-4 shadow-xs"
              >
                <div className="text-[#C5A880] text-3xl sm:text-4xl">
                  <i className={v.icon} aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold text-[#161514] tracking-tight">
                  {v.title}
                </h3>
                <p className="text-sm text-[#5C5852] leading-relaxed">
                  {v.desc}
                </p>
              </div>
            ))}

            {/* Final Highlight Card: Campaign */}
            <div className="bg-[#161514] text-white border border-[#2B2926] rounded-3xl p-8 space-y-6 flex flex-col justify-between">
              <div className="space-y-3">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#C5A880]/20 text-[#C5A880] text-xs font-bold uppercase tracking-wider">
                  <i className="fa-light fa-gift text-xs" aria-hidden="true" />
                  <span>Særtilbud</span>
                </div>
                <h3 className="text-2xl font-bold text-white tracking-tight">
                  Betal for 2 mdr – få 1 mdr gratis
                </h3>
                <p className="text-sm text-[#C7C0B5] leading-relaxed">
                  Få den 3. måned uden beregning. Ingen binding og fuld fleksibilitet.
                </p>
              </div>

              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center gap-2 py-3 px-6 rounded-full bg-[#FAF8F5] text-[#161514] text-sm font-semibold hover:bg-[#EAE4DC] transition-colors"
              >
                <span>Kontakt om tilbuddet</span>
                <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 5-trins Proces */}
      <section className="relative py-20 md:py-28 overflow-hidden bg-[#161514] text-white border-b border-[#2B2926]">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={getOptimizedImageUrl(
              'https://assets.auxodev.dk/assets/d61c94bc-c5ec-439d-a220-afcc00eef7a0/production-editing-studio.png',
              { width: 1800, quality: 80, format: 'auto' }
            )}
            alt="Myserviceforyou proces"
            className="w-full h-full object-cover opacity-35 object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#161514]/85 via-[#161514]/70 to-[#161514]/90" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-xs uppercase tracking-widest text-[#C5A880] font-bold">
              Samarbejdsmodel
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-white">
              Sådan kan samarbejdet se ud
            </h2>
            <p className="text-base text-[#C7C0B5]">
              En enkel og overskuelig proces fra rå idé til færdigt, synligt indhold i jeres Meta-kalender.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {steps.map((step) => (
              <div
                key={step.num}
                className="flex flex-col space-y-3"
              >
                <span className="text-3xl sm:text-4xl font-extrabold text-[#C5A880]">
                  {step.num}
                </span>
                <h3 className="text-lg font-bold text-white">
                  {step.title}
                </h3>
                <p className="text-sm text-[#BDB5A8] leading-relaxed">
                  {step.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/saadan-arbejder-vi"
              className="inline-flex items-center gap-2 text-white text-sm font-semibold hover:text-[#C5A880] transition-colors"
            >
              <span>Læs mere om hvordan samarbejdet fungerer</span>
              <i className="fa-regular fa-arrow-right text-xs transition-transform group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* Cases / Referencer */}
      <section className="py-20 md:py-28 bg-[#FAF8F5] border-b border-[#E8E2D9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div className="space-y-3 max-w-2xl">
              <span className="text-xs uppercase tracking-widest text-[#A8885B] font-bold">
                Tidligere arbejde
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-[#161514]">
                Udvalgte cases og indhold
              </h2>
              <p className="text-base text-[#5C5852]">
                Se eksempler på, hvordan engagerende content skaber synlighed og henvendelser for virksomheder.
              </p>
            </div>
            <Link
              to="/cases"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#161514] text-white text-sm font-semibold hover:bg-[#2A2926] transition-colors shrink-0"
            >
              <span>Se alle cases</span>
              <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          {loadingCases ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-pulse">
              {[1, 2].map((i) => (
                <div key={i} className="aspect-16/10 bg-[#EAE4DC] rounded-3xl" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {cases.map((c) => (
                <Link
                  key={c.id}
                  to={`/cases/${c.case_slug}`}
                  className="group block bg-white border border-[#E5DFD6] rounded-3xl overflow-hidden shadow-xs hover:shadow-md transition-shadow"
                >
                  <div className="aspect-16/10 overflow-hidden bg-[#161514] relative">
                    <img
                      src={getOptimizedImageUrl(c.cover_image?.[0], { width: 900, quality: 80, format: 'auto' })}
                      alt={c.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-103"
                    />
                  </div>

                  <div className="p-6 sm:p-7 flex items-center justify-between gap-4">
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold text-[#161514] group-hover:text-[#A8885B] transition-colors">
                        {c.client_name}
                      </h3>
                      <p className="text-sm text-[#7A746B] mt-1 font-medium group-hover:text-[#161514] transition-colors">
                        {c.industry}
                      </p>
                    </div>
                    <i className="fa-regular fa-arrow-right text-xl text-[#161514] group-hover:translate-x-1.5 transition-transform shrink-0" aria-hidden="true" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Afsluttende CTA */}
      <CtaSection
        title="Klar til at få mere ud af dine sociale medier?"
        subtitle="Alle aftaler laves individuelt gennem kontakt. Ingen faste pakker, 0 binding – og betal for 2 mdr., få 1 mdr. gratis!"
        primaryButtonText="Kontakt os for en snak"
        primaryButtonLink="/kontakt"
        backgroundImage="https://assets.auxodev.dk/assets/d61c94bc-c5ec-439d-a220-afcc00eef7a0/production-editing-studio.png"
      />
    </>
  );
}
